import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTenant } from '../../../../hooks/useTenant';
import FinancialRecordService from '../../../../services/financial_record_service';
import BankAccountService from '../../../../services/bank_account_service';
import AcquirerService from '../../../../services/acquirer_service';
import DebtPaymentService from '../../../../services/debt_payment_service';
import { FINANCIAL_STATUS, FINANCIAL_TYPES, FINANCIAL_SOURCES } from '../../../../constants/financial';

export const useDebtPayment = () => {
    const { id: idMember } = useParams();
    const { idTenant, user } = useTenant();
    const navigate = useNavigate();
    const location = useLocation();

    // The member data from history state or we rely on idMember
    const memberName = location.state?.memberName || "Cliente";
    const memberPhone = location.state?.memberPhone || null;
    const memberEmail = location.state?.memberEmail || null;
    const memberPhotoUrl = location.state?.memberPhotoUrl || null;
    
    const idConsultant = location.state?.idConsultant || null;
    const consultantName = location.state?.consultantName || null;
    const consultantEmail = location.state?.consultantEmail || null;
    const consultantPhone = location.state?.consultantPhone || null;
    const consultantPhotoUrl = location.state?.consultantPhotoUrl || null;

    const [isLoadingData, setIsLoadingData] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);

    const [acquirers, setAcquirers] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);

    const [pendingDebts, setPendingDebts] = useState([]);
    const [, setTotalDebt] = useState(0);

    const [cartItems, setCartItems] = useState([]);
    const [payments, setPayments] = useState([]);

    const [activeTab, setActiveTab] = useState('payment');

    const [saleDate, setSaleDate] = useState(new Date().toISOString().split('T')[0]);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]); // dummy

    // Carregar dados
    useEffect(() => {
        const loadInitialData = async () => {
            if (!idTenant || !idMember) return;
            setIsLoadingData(true);
            try {
                const [acquirersData, bankAccountsData, finData] = await Promise.all([
                    AcquirerService.getAcquirers(idTenant),
                    BankAccountService.getBankAccounts(idTenant),
                    FinancialRecordService.getByMember(idTenant, idMember)
                ]);

                setAcquirers(acquirersData.filter(a => a.status === 'active'));
                setBankAccounts(bankAccountsData.filter(b => b.status === 'active'));

                // Filtrar dívidas do CLIENTE (sourceType "MEMBER" apenas)
                // Parcelas de adquirente (ACQUIRER) não são dívida do aluno.
                const debts = finData.filter(f =>
                    f.type === FINANCIAL_TYPES.INCOME &&
                    (f.status === FINANCIAL_STATUS.PENDING || f.status === FINANCIAL_STATUS.OVERDUE) &&
                    f.sourceType === FINANCIAL_SOURCES.MEMBER
                );
                setPendingDebts(debts);

                const total = debts.reduce((acc, curr) => acc + (curr.netAmount || curr.grossAmount || 0), 0);
                setTotalDebt(total);

                if (total > 0) {
                    setCartItems([{
                        cartId: 'debt-payment',
                        idItem: 'debt',
                        type: 'DEBT',
                        name: 'Quitação de Saldo Devedor',
                        description: `Pagamento de ${debts.length} lançamento(s) pendente(s)`,
                        unitaryValue: total,
                        quantity: 1, // Não dá pra mudar qtd
                        subtotal: total
                    }]);
                } else {
                    toast.info("Não há saldo devedor para este cliente.");
                    navigate(-1);
                }

            } catch (error) {
                console.error("Erro ao carregar dados:", error);
                toast.error("Erro ao carregar saldo devedor.");
            } finally {
                setIsLoadingData(false);
            }
        };

        loadInitialData();
    }, [idTenant, idMember, navigate]);

    // Calcular Totais
    const calculateTotals = () => {
        const subtotal = cartItems.reduce((acc, item) => acc + item.subtotal, 0);
        const totalPaid = payments.reduce((acc, p) => acc + p.value, 0);
        const balance = Math.max(0, subtotal - totalPaid);

        return {
            subtotal,
            discount: 0,
            discountApplied: 0,
            total: subtotal,
            totalPaid,
            balance
        };
    };

    const totals = calculateTotals();

    const handleAddPayment = (paymentMethod) => {
        setPayments(prev => [...prev, { id: Date.now().toString(), ...paymentMethod }]);
    };

    const handleRemovePayment = (idToRemove) => {
        setPayments(prev => prev.filter(p => p.id !== idToRemove));
    };

    const handleRemoveItem = () => {
        toast.warning("Não é possível remover o item de quitação.");
    };

    const handleFinalizeSale = async (dueDate = null) => {
        if (!idTenant || (payments.length === 0 && !dueDate)) {
            toast.warn("Adicione ao menos um pagamento para quitar o saldo ou informe uma data de promessa de pagamento.");
            return;
        }

        setIsProcessing(true);
        try {
            const result = await DebtPaymentService.processDebtPayment({
                idTenant,
                user,
                pendingDebts,
                payments,
                dueDate,
                totalPaid: totals.totalPaid,
                memberData: {
                    id: idMember,
                    name: memberName,
                    phone: memberPhone,
                    email: memberEmail,
                    photoUrl: memberPhotoUrl
                },
                consultantData: {
                    id: idConsultant,
                    name: consultantName,
                    phone: consultantPhone,
                    email: consultantEmail,
                    photoUrl: consultantPhotoUrl
                },
                bankAccounts,
                saleDate
            });

            if (result.isPromiseOnly) {
                toast.success(`Promessa de pagamento registrada para ${result.dueDate}! Uma tarefa foi criada.`);
            } else {
                toast.success("Pagamento de dívida registrado com sucesso!");
            }
            navigate(`/members/${idMember}`);
        } catch (error) {
            console.error("Erro ao quitar saldo:", error);
            toast.error(error.message || "Erro ao processar quitação.");
        } finally {
            setIsProcessing(false);
        }
    };

    return {
        memberName,
        activeTab,
        toggleTab: setActiveTab,
        cartItems,
        payments,
        isProcessing,
        isLoadingData,
        data: { acquirers, memberships: [] },
        totals,
        handleAddPayment,
        handleRemoveItem,
        handleRemovePayment,
        handleFinalizeSale,
        saleDate, setSaleDate,
        startDate, setStartDate,
        discount: 0, setDiscount: () => { }
    };
};
